const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const cleanBridgeText = (bridge = "", anchorTitle = "", currentTitle = "") => {
  let value = String(bridge || "").replace(/\s+/g, " ").trim();
  if (!value) return "";

  const anchor = escapeRegExp(anchorTitle);
  const current = escapeRegExp(currentTitle);
  if (anchor && current) {
    value = value.replace(new RegExp(`^Like\\s+${anchor}\\s*,\\s*${current}\\s+`, "i"), "");
  }
  if (anchor) {
    value = value.replace(new RegExp(`^Like\\s+${anchor}\\s*,\\s*`, "i"), "");
  }
  value = value.replace(/^Like\s+[^,]{2,80},\s*/i, "");

  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
};

const SENTENCE_MARK_PLACEHOLDERS = { ".": "<DOT>", "!": "<BANG>", "?": "<QUESTION>" };

// Titles such as "BanG Dream! YumeMita" contain sentence punctuation. Protect it
// inside known titles. A title's final mark is left alone only where it can end
// a sentence, i.e. before a capital, digit or the end of the text:
// "...Would You Marry Me? It adds..." ends there, but
// "Would You Marry Me? keeps the romance warm..." does not.
// Mirrors maskBridgeSentencePunctuation in the recommendation validator.
const TITLE_MARK_ENDS_SENTENCE_PATTERN = /^["')\]]*(?:\s+[A-Z0-9]|$)/;
const protectMarks = (value) => value.replace(/[.!?]/g, (mark) => SENTENCE_MARK_PLACEHOLDERS[mark]);

const protectTitlePunctuation = (text, titles = []) => [...new Set(
  (Array.isArray(titles) ? titles : [])
    .map((title) => String(title || "").replace(/\s+/g, " ").trim())
    .filter((title) => title.length > 2)
)]
  .sort((left, right) => right.length - left.length)
  .reduce((current, title) => current.replace(
    new RegExp(escapeRegExp(title), "gi"),
    (match, offset, whole) => (TITLE_MARK_ENDS_SENTENCE_PATTERN.test(whole.slice(offset + match.length))
      ? protectMarks(match.slice(0, -1)) + match.slice(-1)
      : protectMarks(match))
  ), text);

export const firstCompleteSentence = (value = "", titles = []) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";

  const protectedText = protectTitlePunctuation(text, titles)
    .replace(/\b([A-Z]\.){2,}/g, (match) => match.replaceAll(".", "<DOT>"));
  const match = protectedText.match(/^.*?[.!?](?=\s|$)/);
  return Object.entries(SENTENCE_MARK_PLACEHOLDERS).reduce(
    (current, [mark, placeholder]) => current.replaceAll(placeholder, mark),
    match ? match[0] : protectedText
  );
};

export const bridgePreviewText = (bridge = "", anchorTitle = "", currentTitle = "") =>
  firstCompleteSentence(cleanBridgeText(bridge, anchorTitle, currentTitle), [anchorTitle, currentTitle]);

export const bridgeTextParts = (value = "", titles = []) => {
  const text = String(value || "");
  const titlePatterns = [...new Set(
    (Array.isArray(titles) ? titles : [])
      .map((title) => String(title || "").replace(/\s+/g, " ").trim())
      .filter((title) => title.length > 2)
  )].sort((left, right) => right.length - left.length);

  if (!text || titlePatterns.length === 0) {
    return [{ text, isTitle: false }];
  }

  const titlePattern = titlePatterns.map(escapeRegExp).join("|");
  const titleRegex = new RegExp(`(^|[^\\p{L}\\p{N}])(${titlePattern})(?=$|[^\\p{L}\\p{N}])`, "giu");
  const parts = [];
  let cursor = 0;
  let match;

  while ((match = titleRegex.exec(text)) !== null) {
    const prefix = match[1] || "";
    const title = match[2] || "";
    const titleStart = match.index + prefix.length;
    const titleEnd = titleStart + title.length;

    if (titleStart > cursor) {
      parts.push({ text: text.slice(cursor, titleStart), isTitle: false });
    }
    parts.push({ text: text.slice(titleStart, titleEnd), isTitle: true });
    cursor = titleEnd;
  }

  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), isTitle: false });
  }

  return parts.length > 0 ? parts : [{ text, isTitle: false }];
};
